import { Fragment, useEffect, useState } from 'react'
import { Building2, Calendar, Car, Fuel, MapPin, ListChecks, Store, FileText, FileSpreadsheet } from 'lucide-react'
import api from '../services/api'
import LookupSelect from '../components/LookupSelect'
import { useEmpresa } from '../contexts/EmpresaContext'
import { Linha } from './LancamentoDepositos'
import { SeletorMultiplo } from './RelatorioConsumoArla'

interface Lancamento {
  data: string; hora: number; numero: number; veicod: string; litros: number; valor: number; vun: number
  desconto: number; ctf: string; letra: string; combustivel: string; hodometro: number
}
interface Grupo { nome: string; litros: number; valor: number; desconto: number; vun: number | null }
interface Posto { pracod: string; pranom: string; cidade: string; cnpj: string; ctf: string; uf: string; itens: Lancamento[]; grupos: Grupo[]; valor: number; desconto: number }
interface Resultado { postos: Posto[]; total: { grupos: Grupo[]; valor: number; desconto: number; registros: number } }
interface Opcao { cod: string; des: string }

const fmtN = (v: number, c: number) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: c, maximumFractionDigits: c })
const br = (d: string) => d.split('-').reverse().join('/')
const fmtHora = (h: number) => { const [hh, mm = '00'] = Number(h || 0).toFixed(2).split('.'); return `${hh.padStart(2, '0')}:${mm}` }
const fmtCnpj = (c: string) => { const d = (c || '').replace(/\D/g, ''); if (!d) return ''; const p = d.padStart(14, '0'); return `${p.slice(0, 2)}.${p.slice(2, 5)}.${p.slice(5, 8)}/${p.slice(8, 12)}-${p.slice(12)}` }
const hojeIso = () => new Date().toISOString().slice(0, 10)

// Lançamentos de combustível por posto (GeneXus WOCOMPRG → RDPS039), analítico ou sintético.
export default function RelatorioConsumoPosto() {
  const { empresas } = useEmpresa()
  const [dataIni, setDataIni] = useState(hojeIso())
  const [dataFim, setDataFim] = useState(hojeIso())
  const [empresasSel, setEmpresasSel] = useState<string[]>([])
  const [posto, setPosto] = useState('')
  const [veiculo, setVeiculo] = useState('')
  const [combustiveisSel, setCombustiveisSel] = useState<string[]>([])
  const [tipoRel, setTipoRel] = useState<'S' | 'A'>('S')
  const [postos, setPostos] = useState<Opcao[]>([])
  const [combustiveis, setCombustiveis] = useState<Opcao[]>([])
  const [res, setRes] = useState<Resultado | null>(null)
  const [resTipo, setResTipo] = useState<'S' | 'A'>('S')
  const [carregando, setCarregando] = useState(false)
  const [gerando, setGerando] = useState<'' | 'pdf' | 'excel'>('')
  const [erro, setErro] = useState('')

  useEffect(() => {
    api.get('/combustivel/postos').then(r => setPostos(r.data.map((p: any) => ({ cod: p.pracod, des: `${p.pracod} — ${p.pranom}` })))).catch(() => {})
    api.get('/combustivel/combustiveis-posto').then(r => setCombustiveis(r.data.filter((c: any) => c.letra !== 'I').map((c: any) => ({ cod: c.letra, des: `${c.letra} — ${c.nome}` })))).catch(() => {})
  }, [])

  const params = () => ({ dataIni, dataFim, empresas: empresasSel.join(','), posto, veiculo, combustiveis: combustiveisSel.join(','), tipoRel })

  async function gerar() {
    setErro(''); setCarregando(true)
    try { setRes((await api.get('/combustivel/relatorios/consumo-posto', { params: params() })).data); setResTipo(tipoRel) }
    catch (e: any) { setRes(null); setErro(e?.response?.data?.error || 'Erro ao gerar relatório.') }
    finally { setCarregando(false) }
  }

  async function exportar(formato: 'pdf' | 'excel') {
    setErro(''); setGerando(formato)
    try {
      const r = await api.get(`/combustivel/relatorios/consumo-posto/${formato}`, { params: params(), responseType: 'blob' })
      const tipoArq = formato === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      const url = URL.createObjectURL(new Blob([r.data], { type: tipoArq }))
      if (formato === 'pdf') window.open(url, '_blank')
      else {
        const a = document.createElement('a'); a.href = url
        a.download = `lancamentos-por-posto-${tipoRel === 'S' ? 'sintetico' : 'analitico'}.xlsx`
        a.click(); URL.revokeObjectURL(url)
      }
    } catch (e: any) {
      // Erro de validação vem como blob JSON.
      try { const t = await e?.response?.data?.text?.(); setErro(JSON.parse(t).error) } catch { setErro(formato === 'pdf' ? 'Erro ao gerar PDF.' : 'Erro ao gerar Excel.') }
    } finally { setGerando('') }
  }

  const th: React.CSSProperties = { padding: '0.4rem 0.45rem', fontSize: '0.66rem', fontWeight: 800, color: '#fff', background: '#1a6ea8', whiteSpace: 'nowrap', textAlign: 'right', position: 'sticky', top: 0, zIndex: 1 }
  const td: React.CSSProperties = { padding: '0.28rem 0.45rem', fontSize: '0.74rem', borderBottom: '1px solid #eef2f7', whiteSpace: 'nowrap', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }
  const esq: React.CSSProperties = { ...td, textAlign: 'left' }
  const analitico = resTipo === 'A'
  const nCols = analitico ? 12 : 5

  // Subtotais por combustível e total (posto ou geral), alinhados nas colunas Litros/Valor/V.Unit/Desconto.
  const linhasTotais = (grupos: Grupo[], valor: number, desconto: number, rotulo: string, geral = false) => {
    const pre = analitico ? 4 : 1
    const pos = analitico ? 4 : 0
    const cor = geral ? '#0f2a52' : '#1b3f7b'
    return (
      <>
        {grupos.map(g => (
          <tr key={g.nome}>
            <td colSpan={pre} style={{ ...td, fontWeight: 700, color: '#475569' }}>{g.nome}</td>
            <td style={td}>{fmtN(g.litros, 3)}</td>
            <td style={td}>{fmtN(g.valor, 2)}</td>
            <td style={td}>{fmtN(g.vun ?? 0, 3)}</td>
            <td style={td}>{fmtN(g.desconto, 2)}</td>
            {pos > 0 && <td colSpan={pos} style={td} />}
          </tr>
        ))}
        <tr style={{ background: geral ? '#dbeafe' : '#eff6ff' }}>
          <td colSpan={pre + 1} style={{ ...td, fontWeight: 800, color: cor, borderBottom: '2px solid #bfdbfe' }}>{rotulo}</td>
          <td style={{ ...td, fontWeight: 800, color: cor, borderBottom: '2px solid #bfdbfe' }}>{fmtN(valor, 2)}</td>
          <td style={{ ...td, borderBottom: '2px solid #bfdbfe' }} />
          <td style={{ ...td, fontWeight: 800, color: cor, borderBottom: '2px solid #bfdbfe' }}>{fmtN(desconto, 2)}</td>
          {pos > 0 && <td colSpan={pos} style={{ ...td, borderBottom: '2px solid #bfdbfe' }} />}
        </tr>
      </>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
      <div className="modal-box" style={{ width: '100%', maxWidth: 680, maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
        <div className="modal-header" style={{ background: 'linear-gradient(135deg, #1a6ea8, #14547f)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 9, background: 'rgba(255,255,255,0.15)' }}>
              <Store size={18} color="#fff" />
            </span>
            <div>
              <h2>Lançamentos por Posto</h2>
              <div style={{ fontSize: '0.7rem', color: '#dbeafe', marginTop: 2 }}>Abastecimentos agrupados por posto, com subtotal por combustível</div>
            </div>
          </div>
        </div>

        <div className="modal-corpo">
          <Linha label="Período" icon={Calendar}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <input type="date" value={dataIni} onChange={e => setDataIni(e.target.value)} style={{ maxWidth: 170 }} />
              <span style={{ fontSize: '0.8rem', color: '#57606f' }}>a</span>
              <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} style={{ maxWidth: 170 }} />
            </div>
          </Linha>
          <Linha label="Estabelecimentos" icon={Building2}>
            <SeletorMultiplo opcoes={empresas.map(e => ({ cod: String(e.empcod), des: `${e.empcod} — ${e.empfan?.trim() || e.empnom?.trim()}` }))}
              selecionados={empresasSel} onChange={setEmpresasSel} placeholderBusca="Buscar empresa..." />
          </Linha>
          <Linha label="Posto" icon={MapPin}>
            <select value={posto} onChange={e => setPosto(e.target.value)} style={{ maxWidth: 320 }}>
              <option value="">Todos</option>
              {postos.map(p => <option key={p.cod} value={p.cod}>{p.des}</option>)}
            </select>
          </Linha>
          <Linha label="Veículo" icon={Car}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <div style={{ maxWidth: 140 }}>
                <LookupSelect endpoint="/erp/veiculos" codKey="veicod" descKey="veipla" tagKey="veicod"
                  codValue={veiculo} descValue={veiculo} onSelect={cod => setVeiculo(cod)} placeholder="Todos" />
              </div>
              {veiculo
                ? <button className="btn btn-secondary" style={{ padding: '0.25rem 0.6rem', fontSize: '0.72rem' }} onClick={() => setVeiculo('')}>Todos</button>
                : <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>em branco = todos</span>}
            </div>
          </Linha>
          <Linha label="Combustíveis" icon={Fuel}>
            <SeletorMultiplo opcoes={combustiveis} selecionados={combustiveisSel} onChange={setCombustiveisSel} placeholderBusca="Buscar combustível..." />
          </Linha>
          <Linha label="Tipo Relatório" icon={ListChecks}>
            <select value={tipoRel} onChange={e => setTipoRel(e.target.value as 'S' | 'A')} style={{ maxWidth: 200 }}>
              <option value="S">Sintético</option>
              <option value="A">Analítico</option>
            </select>
          </Linha>
          {erro && (
            <div style={{ marginTop: '0.9rem', fontSize: '0.8rem', fontWeight: 600, padding: '0.55rem 0.9rem', borderRadius: 8, background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' }}>{erro}</div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-primary" onClick={gerar} disabled={carregando}>{carregando ? 'Gerando...' : 'Gerar'}</button>
          <button className="btn" style={{ background: '#dc2626', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }} onClick={() => exportar('pdf')} disabled={!!gerando}>
            <FileText size={15} /> {gerando === 'pdf' ? 'Gerando PDF...' : 'PDF'}
          </button>
          <button className="btn" style={{ background: '#15803d', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }} onClick={() => exportar('excel')} disabled={!!gerando}>
            <FileSpreadsheet size={15} /> {gerando === 'excel' ? 'Gerando Excel...' : 'Excel'}
          </button>
        </div>
      </div>

      {res && (
        <div className="modal-box" style={{ width: '100%', maxWidth: 'none', maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
          <div className="modal-corpo">
            <div style={{ display: 'flex', gap: '1.2rem', flexWrap: 'wrap', fontSize: '0.8rem', color: '#475569', marginBottom: '0.7rem' }}>
              <span><b>{res.postos.length}</b> posto(s)</span>
              <span><b>{res.total.registros}</b> lançamento(s)</span>
              <span>Valor: <b style={{ color: '#1b3f7b' }}>R$ {fmtN(res.total.valor, 2)}</b></span>
            </div>
            <div style={{ overflow: 'auto', maxHeight: '70vh', border: '1px solid #bfdbfe', borderRadius: 8 }}>
              <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
                <thead>
                  <tr>
                    {analitico
                      ? ['Data', 'Hora', 'Número', 'Veículo', 'Litros', 'Valor', 'V.Unit', 'Desconto', 'Ctf', 'Pos', 'Combus', 'Hodômetro'].map(c => (
                        <th key={c} style={{ ...th, textAlign: ['Data', 'Hora', 'Veículo', 'Ctf', 'Pos', 'Combus'].includes(c) ? 'left' : 'right' }}>{c}</th>))
                      : ['', 'Litros', 'Valor', 'V.Unit', 'Desconto'].map(c => <th key={c || 'grupo'} style={th}>{c}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {res.postos.map(p => (
                    <Fragment key={p.pracod}>
                      <tr>
                        <td colSpan={nCols} style={{ ...esq, fontWeight: 800, color: '#b91c1c', background: '#fef2f2' }}>
                          {p.pracod} — {p.pranom}
                          <span style={{ fontWeight: 600, color: '#7f1d1d', marginLeft: 12 }}>
                            {[p.cidade, p.cnpj ? `CNPJ ${fmtCnpj(p.cnpj)}` : '', p.ctf ? `CTF ${p.ctf}` : '', p.uf].filter(Boolean).join('  •  ')}
                          </span>
                        </td>
                      </tr>
                      {analitico && p.itens.map((i, k) => (
                        <tr key={`${i.numero}-${k}`} style={{ background: k % 2 ? '#f5f9fd' : '#fff' }}>
                          <td style={{ ...esq, color: '#1a6ea8' }}>{br(i.data)}</td>
                          <td style={{ ...esq, color: '#7c3aed' }}>{fmtHora(i.hora)}</td>
                          <td style={td}>{i.numero}</td>
                          <td style={{ ...esq, fontWeight: 700, color: '#1a6ea8' }}>{i.veicod}</td>
                          <td style={td}>{fmtN(i.litros, 3)}</td>
                          <td style={td}>{fmtN(i.valor, 2)}</td>
                          <td style={td}>{fmtN(i.vun, 3)}</td>
                          <td style={td}>{fmtN(i.desconto, 2)}</td>
                          <td style={esq}>{i.ctf}</td>
                          <td style={{ ...esq, color: '#b91c1c' }}>{p.pracod}</td>
                          <td style={{ ...esq, color: '#b91c1c' }}>{i.combustivel}</td>
                          <td style={td}>{fmtN(i.hodometro, 0)}</td>
                        </tr>
                      ))}
                      {linhasTotais(p.grupos, p.valor, p.desconto, 'Total Posto')}
                    </Fragment>
                  ))}
                  {res.postos.length > 0 && (
                    <>
                      <tr><td colSpan={nCols} style={{ ...esq, fontWeight: 800, color: '#fff', background: '#1b3f7b' }}>TOTAL GERAL</td></tr>
                      {linhasTotais(res.total.grupos, res.total.valor, res.total.desconto, 'Total Geral', true)}
                    </>
                  )}
                  {res.postos.length === 0 && <tr><td colSpan={nCols} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: '1.2rem' }}>Nenhum lançamento no período.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
